import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, adminAuth } from "@/lib/adminApi";
import { ADMIN } from "@/constants/testIds";
import { LOGO_URL } from "@/content/veguk";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, LogOut, RefreshCw, Mail, MessageSquare, FileText, CheckCircle2, Clock, Download } from "lucide-react";
import { toast } from "sonner";

function formatDate(d) {
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [pdfLeads, setPdfLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("contacts");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, s, p] = await Promise.all([
        adminApi.get("/admin/contacts"),
        adminApi.get("/admin/subscribers"),
        adminApi.get("/admin/pdf-leads"),
      ]);
      setContacts(c.data);
      setSubscribers(s.data);
      setPdfLeads(p.data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error("Could not load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    adminAuth.logout();
    navigate("/admin/login", { replace: true });
  };

  const removeContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await adminApi.delete(`/admin/contacts/${id}`);
      setContacts((prev) => prev.filter((x) => x.id !== id));
      toast.success("Message deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const removeSubscriber = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await adminApi.delete(`/admin/subscribers/${id}`);
      setSubscribers((prev) => prev.filter((x) => x.id !== id));
      toast.success("Subscriber removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportSubscribersCsv = async () => {
    try {
      const res = await adminApi.get("/admin/subscribers.csv", {
        responseType: "blob",
      });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match?.[1] || "veguk-subscribers.csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${subscribers.length} subscriber${subscribers.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const removePdfLead = async (id) => {
    if (!window.confirm("Remove this PDF request?")) return;
    try {
      await adminApi.delete(`/admin/pdf-leads/${id}`);
      setPdfLeads((prev) => prev.filter((x) => x.id !== id));
      toast.success("PDF request removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div
      data-testid={ADMIN.dashboardRoot}
      className="min-h-screen bg-[#F9F6F0] text-[#2C352D]"
    >
      <header className="border-b border-[#D1C9B9] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="VegUK home">
            <img
              src={LOGO_URL}
              alt="VegUK"
              className="h-12 w-12 rounded-full bg-white border border-[#D1C9B9] p-1"
            />
            <div>
              <div className="font-serif-display text-xl font-bold text-[#2C352D]">VegUK</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#2E5C46]">
                Admin dashboard
              </div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#4A5D4E]">
              {adminAuth.email()}
            </span>
            <Button
              variant="outline"
              onClick={fetchAll}
              className="border-[#D1C9B9] bg-transparent text-[#2E5C46] hover:bg-[#EAE3D9]"
            >
              <RefreshCw size={14} className="mr-2" /> Refresh
            </Button>
            <Button
              data-testid={ADMIN.logoutBtn}
              onClick={logout}
              className="bg-[#2E5C46] hover:bg-[#234836] text-[#F9F6F0]"
            >
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-serif-display text-4xl text-[#2E5C46] mb-2">
          Overview
        </h1>
        <p className="text-[#4A5D4E] mb-8">
          Manage incoming contact messages and newsletter subscribers.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#D1C9B9] rounded-lg p-6 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#2E5C46]">
                Messages
              </div>
              <div className="font-serif-display text-4xl text-[#2C352D] mt-2">
                {contacts.length}
              </div>
            </div>
            <MessageSquare size={28} className="text-[#2E5C46]" />
          </div>
          <div className="bg-white border border-[#D1C9B9] rounded-lg p-6 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#2E5C46]">
                Subscribers
              </div>
              <div className="font-serif-display text-4xl text-[#2C352D] mt-2">
                {subscribers.length}
              </div>
            </div>
            <Mail size={28} className="text-[#2E5C46]" />
          </div>
          <div className="bg-white border border-[#D1C9B9] rounded-lg p-6 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#2E5C46]">
                PDF requests
              </div>
              <div className="font-serif-display text-4xl text-[#2C352D] mt-2">
                {pdfLeads.length}
              </div>
            </div>
            <FileText size={28} className="text-[#2E5C46]" />
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#EAE3D9] border border-[#D1C9B9]">
            <TabsTrigger
              data-testid={ADMIN.tabContacts}
              value="contacts"
              className="data-[state=active]:bg-white data-[state=active]:text-[#2E5C46] font-medium"
            >
              Messages ({contacts.length})
            </TabsTrigger>
            <TabsTrigger
              data-testid={ADMIN.tabSubscribers}
              value="subscribers"
              className="data-[state=active]:bg-white data-[state=active]:text-[#2E5C46] font-medium"
            >
              Subscribers ({subscribers.length})
            </TabsTrigger>
            <TabsTrigger
              data-testid={ADMIN.tabPdfLeads}
              value="pdfleads"
              className="data-[state=active]:bg-white data-[state=active]:text-[#2E5C46] font-medium"
            >
              PDF requests ({pdfLeads.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="mt-5">
            <div
              data-testid={ADMIN.contactsTable}
              className="bg-white border border-[#D1C9B9] rounded-lg overflow-x-auto"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#4A5D4E] py-10">
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && contacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#4A5D4E] py-10">
                        No messages yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {contacts.map((c) => (
                    <TableRow key={c.id} data-testid={ADMIN.contactRow(c.id)}>
                      <TableCell className="whitespace-nowrap text-xs text-[#4A5D4E]">
                        {formatDate(c.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>
                        <a
                          href={`mailto:${c.email}`}
                          className="text-[#2E5C46] hover:underline"
                        >
                          {c.email}
                        </a>
                      </TableCell>
                      <TableCell className="text-[#4A5D4E]">{c.organisation || "—"}</TableCell>
                      <TableCell className="max-w-md text-sm text-[#2C352D]">
                        <span className="line-clamp-2">{c.message}</span>
                      </TableCell>
                      <TableCell>
                        <button
                          data-testid={ADMIN.deleteContact(c.id)}
                          onClick={() => removeContact(c.id)}
                          className="text-[#A34E36] hover:text-[#7a3826] p-1"
                          aria-label="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="subscribers" className="mt-5">
            <div className="flex justify-end mb-3">
              <Button
                data-testid={ADMIN.exportSubscribersCsv}
                onClick={exportSubscribersCsv}
                disabled={subscribers.length === 0}
                variant="outline"
                className="border-[#2E5C46] bg-white text-[#2E5C46] hover:bg-[#EAE3D9] disabled:opacity-40"
              >
                <Download size={14} className="mr-2" /> Download CSV
              </Button>
            </div>
            <div
              data-testid={ADMIN.subscribersTable}
              className="bg-white border border-[#D1C9B9] rounded-lg overflow-x-auto"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subscribed</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-[#4A5D4E] py-10">
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && subscribers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-[#4A5D4E] py-10">
                        No subscribers yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {subscribers.map((s) => (
                    <TableRow key={s.id} data-testid={ADMIN.subscriberRow(s.id)}>
                      <TableCell className="whitespace-nowrap text-xs text-[#4A5D4E]">
                        {formatDate(s.created_at)}
                      </TableCell>
                      <TableCell>
                        <a
                          href={`mailto:${s.email}`}
                          className="text-[#2E5C46] hover:underline"
                        >
                          {s.email}
                        </a>
                      </TableCell>
                      <TableCell>
                        <button
                          data-testid={ADMIN.deleteSubscriber(s.id)}
                          onClick={() => removeSubscriber(s.id)}
                          className="text-[#A34E36] hover:text-[#7a3826] p-1"
                          aria-label="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="pdfleads" className="mt-5">
            <div
              data-testid={ADMIN.pdfLeadsTable}
              className="bg-white border border-[#D1C9B9] rounded-lg overflow-x-auto"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requested</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-[#4A5D4E] py-10">
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && pdfLeads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-[#4A5D4E] py-10">
                        No PDF requests yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {pdfLeads.map((p) => (
                    <TableRow key={p.id} data-testid={ADMIN.pdfLeadRow(p.id)}>
                      <TableCell className="whitespace-nowrap text-xs text-[#4A5D4E]">
                        {formatDate(p.created_at)}
                      </TableCell>
                      <TableCell>
                        <a
                          href={`mailto:${p.email}`}
                          className="text-[#2E5C46] hover:underline"
                        >
                          {p.email}
                        </a>
                      </TableCell>
                      <TableCell>
                        {p.delivered ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-[#2E5C46]">
                            <CheckCircle2 size={14} /> Delivered
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-[#C59346]">
                            <Clock size={14} /> Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <button
                          data-testid={ADMIN.deletePdfLead(p.id)}
                          onClick={() => removePdfLead(p.id)}
                          className="text-[#A34E36] hover:text-[#7a3826] p-1"
                          aria-label="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
